import React, { JSX } from 'react';
import { Link as StyledLink, utils } from '@makeship/core';
import styled from 'styled-components';
import { scroller } from 'react-scroll';
import posthog from 'posthog-js';
import config from '../../../../config.json';
import { ProductStage, ProductTag } from '../../../types/common';
import {
  getCampaignStageTag,
  getProductIDFromShopifyGID,
  getStage,
  hasProductTag,
  isCurrentDateBeforeGivenMetafieldDate,
  parseMetafields,
  getVariantShipDateInfo,
  getEffectiveShipDate,
} from '../../../utils/product';
import { S2, S1, P2, Caption, H9 } from '../../Typography';
import { getPDPTrackingLocation, handleSupportLinkTracking, isUserLoggedIn } from '../../../utils/analytics';

import { getProductFromContext } from '../../../context/product';

const { routes } = config;

const ShipDateContainer = styled.div<{ isInInfoDrawer?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 12px;
  margin-bottom: 24px;

  ${({ isInInfoDrawer }) => isInInfoDrawer && `margin: 0px;`}
`;

const ShipDateContainerPast = styled(ShipDateContainer)`
  align-items: start;
`;

const ShipDateWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const HybridPreOrderCopyText = styled(Caption)`
  display: inline;
`;

const HybridPreOrderH9Text = styled(H9)`
  display: inline;
`;

const ShipDateText = styled(S2)`
  margin-left: 8px;
`;

const ShipDateTextInDrawer = styled(P2)<{ isInInfoDrawer: boolean }>`
  margin-left: 8px;

  ${({ isInInfoDrawer }) => isInInfoDrawer && `font-weight: 700;`}
`;

const ShipDateTextPast = styled(S1)`
  margin-left: 8px;
`;

const HybridMessagingContainer = styled.div<{ isReadyToShip?: boolean }>`
  width: 100%;
  padding: 18px;
  background: ${({ isReadyToShip, theme }) => (isReadyToShip ? theme.colors.R2SGreen : theme.colors.PreOrderPurple)};
  border-radius: 10px;
  margin-top: 16px;
`;

const HelpCenterLink = styled(StyledLink.Primary)``;

export const getSubscribeLabel = (stage: ProductStage, isSoldOut: boolean, isEvergreen: boolean): string => {
  switch (stage) {
    case ProductStage.Failed:
      return `Enter your email to receive updates about future ${isEvergreen ? 'products' : 'campaigns'}.`;
    case ProductStage.Live:
      if (isSoldOut) {
        return `Enter your email to get notified ${
          isEvergreen ? 'when this product' : 'if this product ever'
        } comes back... 👀`;
      }
      return `Enter your email to receive updates about this ${isEvergreen ? 'product' : 'campaign'}!`;
    default:
      return `Enter your email to receive updates about this ${isEvergreen ? 'product' : 'campaign'}!`;
  }
};

export const createEvergreenTags = (product: Shopify.Product): string | undefined =>
  product.id ? `${getProductIDFromShopifyGID(product.id)}-evergreen-waitlist` : undefined;

export const createTags = (
  product: Shopify.Product,
  creatorCollectionHandle: string | null,
  totalInventory: number,
  moq: number,
  isSoldOut: boolean,
): string | undefined => {
  const stage = getStage(product.tags);

  // We will almost always return this
  if (product.id && creatorCollectionHandle) {
    return `${product.title}, ${creatorCollectionHandle}, ${getCampaignStageTag(
      stage,
      totalInventory,
      moq,
      isSoldOut,
    )}, subscribed-product`;
  }
  // We need this to handle empty tags
  if (!product.id && !creatorCollectionHandle) {
    return undefined;
  }
  return creatorCollectionHandle
    ? `${creatorCollectionHandle}, subscribed-product`
    : `${product.title}, ${getCampaignStageTag(stage, totalInventory, moq, isSoldOut)}, subscribed-product`;
};

export const scrollToCustomTarget = (target: string, offset: number) => {
  scroller.scrollTo(target, {
    duration: 800,
    smooth: 'easeInOutQuad',
    offset,
  });
};

export const shouldRenderShippingInfo = () => {
  const product = getProductFromContext();
  const stage = getStage(product.tags);
  const isLimited = hasProductTag(product.tags, ProductTag.Limited);
  const isEvergreen = hasProductTag(product.tags, ProductTag.Evergreen);
  const metafields = parseMetafields(product.metafields);

  if (!isLimited && !isEvergreen && !metafields.delivery) {
    return false;
  }

  return stage === ProductStage.Live || stage === ProductStage.Draft;
};

export const isHybridPreOrder = (product: Shopify.Product): boolean => {
  const metafields = parseMetafields(product.metafields);
  const isHybrid = hasProductTag(product.tags, ProductTag.Hybrid);

  if (!isHybrid) return false;

  // Check variant-level ship dates first (takes precedence)
  const { variantShipDates } = getVariantShipDateInfo(product);

  if (variantShipDates.length > 0) {
    // All variants must be pre-order (future ship dates)
    return variantShipDates.every((date) => isCurrentDateBeforeGivenMetafieldDate(date));
  }

  // Fall back to product-level delivery date if no variant dates exist
  if (metafields.delivery) {
    return isCurrentDateBeforeGivenMetafieldDate(metafields.delivery);
  }

  return false;
};

export const renderShippingDate = (
  delivery: string | null | undefined,
  stage: ProductStage,
  isInInfoDrawer = false,
  product: Shopify.Product,
  isHybridPreOrder?: boolean,
  user?: User,
  selectedVariant?: Shopify.ProductVariant,
): JSX.Element => {
  const isLimited = hasProductTag(product.tags, ProductTag.Limited);
  const isEvergreen = hasProductTag(product.tags, ProductTag.Evergreen);
  const isHybrid = hasProductTag(product.tags, ProductTag.Hybrid);

  // Show order confirmation message for sold-out hybrid products
  if (isHybridSoldOut(product)) {
    return (
      <ShipDateContainerPast isInInfoDrawer={isInInfoDrawer}>
        <ShipDateWrapper>
          📦
          <ShipDateText>Check your order confirmation email for shipping information</ShipDateText>
        </ShipDateWrapper>
      </ShipDateContainerPast>
    );
  }

  if (!isLimited && !isEvergreen && !delivery) {
    return <></>;
  }
  const handleShippingTimelinesClick = () => {
    scrollToCustomTarget('shipping-info', -200);
    posthog.capture('hybrid_shipping_timelines_click', {
      product_id: getProductIDFromShopifyGID(product.id),
      product_title: product.title,
      product_stage: stage,
      is_logged_in: isUserLoggedIn(user),
    });
  };

  const metafields = parseMetafields(product.metafields);
  const hasHybridShippingInfo = metafields.hybridShippingInfo && metafields.hybridShippingInfo.length > 0;

  // Get variant ship date information
  const { hasDifferentShipDates, allVariantsReadyToShip } = getVariantShipDateInfo(product);

  // Get effective ship date (variant-specific or product-level)
  const effectiveShipDate = getEffectiveShipDate(product, selectedVariant, delivery);

  // Check if effective date is pre-order (future) or ready-to-ship (past)
  const isEffectivePreOrder = effectiveShipDate ? isCurrentDateBeforeGivenMetafieldDate(effectiveShipDate) : false;

  if (stage !== ProductStage.Past && isInInfoDrawer && effectiveShipDate) {
    return (
      <ShipDateContainer isInInfoDrawer={isInInfoDrawer}>
        <ShipDateWrapper>
          📦
          <ShipDateTextInDrawer isInInfoDrawer={isInInfoDrawer}>
            Ships {utils.getDate(effectiveShipDate)}
          </ShipDateTextInDrawer>
        </ShipDateWrapper>
      </ShipDateContainer>
    );
  }

  // Render helper for hybrid messaging container
  const renderHybridMessage = (isReadyToShip: boolean, shipDate: string | Date, linkText: string) => {
    const shouldShowLink = isReadyToShip || hasHybridShippingInfo;

    return (
      <HybridMessagingContainer isReadyToShip={isReadyToShip}>
        <S2>📦 {isReadyToShip ? 'Ready-to-ship' : 'Pre-order'}</S2>
        <HybridPreOrderCopyText>Orders placed today are estimated to ship on </HybridPreOrderCopyText>
        <HybridPreOrderH9Text>{utils.getDate(shipDate.toString())}</HybridPreOrderH9Text>.
        {shouldShowLink && hasHybridShippingInfo && (
          <>
            <HybridPreOrderH9Text>
              {` `}
              <StyledLink.Primary onClick={handleShippingTimelinesClick}>{linkText}</StyledLink.Primary>
            </HybridPreOrderH9Text>
            {linkText === 'See shipping timelines' && (
              <HybridPreOrderCopyText> below for details on past orders.</HybridPreOrderCopyText>
            )}
          </>
        )}
        {shouldShowLink && isHybrid && !hasHybridShippingInfo && (
          <HybridPreOrderH9Text>
            {` `}
            <StyledLink.Primary onClick={handleShippingTimelinesClick}>{linkText}</StyledLink.Primary>
          </HybridPreOrderH9Text>
        )}
      </HybridMessagingContainer>
    );
  };

  // Handle hybrid campaigns with different variant ship dates
  if (isHybrid && hasDifferentShipDates) {
    // If all variants are ready-to-ship, show ready-to-ship banner
    if (allVariantsReadyToShip) {
      return renderHybridMessage(true, calculateReadyToShipDate, 'Did you already purchase this product?');
    }

    // If no variant selected, show "Choose a size" message
    if (!selectedVariant) {
      return (
        <ShipDateContainer isInInfoDrawer={isInInfoDrawer}>
          <ShipDateWrapper>
            📦
            <ShipDateText>Choose a size to see ship date</ShipDateText>
          </ShipDateWrapper>
        </ShipDateContainer>
      );
    }

    // Show variant-specific ship date based on pre-order status
    if (effectiveShipDate) {
      const linkText = !isEffectivePreOrder ? 'Did you already purchase this product?' : 'See shipping timelines';
      return renderHybridMessage(
        !isEffectivePreOrder,
        isEffectivePreOrder ? effectiveShipDate : calculateReadyToShipDate,
        linkText,
      );
    }
  }

  // Handle hybrid campaigns without variant-specific ship dates (use product-level date)
  if (isHybridPreOrder && effectiveShipDate) {
    return renderHybridMessage(false, effectiveShipDate, 'See shipping timelines');
  }

  if (isReadyToShip(product)) {
    const linkText = isHybrid ? 'Did you already purchase this product?' : 'See shipping timelines';
    return renderHybridMessage(true, calculateReadyToShipDate, linkText);
  }

  return stage === ProductStage.Past && effectiveShipDate ? (
    <ShipDateContainerPast>
      <ShipDateWrapper>
        📦
        <ShipDateTextPast>Ships {utils.getDate(effectiveShipDate)}</ShipDateTextPast>
      </ShipDateWrapper>
      {renderLearnMoreShippingLink(product)}
    </ShipDateContainerPast>
  ) : effectiveShipDate ? (
    <ShipDateContainer isInInfoDrawer={isInInfoDrawer}>
      <ShipDateWrapper>
        📦
        <ShipDateText>Ships {utils.getDate(effectiveShipDate)}</ShipDateText>
      </ShipDateWrapper>
    </ShipDateContainer>
  ) : (
    <></>
  );
};

export const renderLearnMoreShippingLink = (
  product: Shopify.Product,
  alternateEventTrackingLocation?: string,
): JSX.Element => (
  <HelpCenterLink
    target="_blank"
    href={`${routes.helpCenter}/articles/7989946643085-When-will-my-order-ship-`}
    rel="noopener noreferrer"
    data-testid="help-center"
    onClick={() => {
      const eventTrackingLocation = alternateEventTrackingLocation || getPDPTrackingLocation(product);
      handleSupportLinkTracking('shipping_info', eventTrackingLocation, product);
    }}
  >
    Learn more about our shipping timelines
  </HelpCenterLink>
);

export const renderSupportLink = (product: Shopify.Product): JSX.Element => (
  <HelpCenterLink
    target="_blank"
    href="https://support.makeship.com/hc/en-us?_gl=1*ofbirr*_gcl_aw*R0NMLjE3MTIwNjU4NjkuQ2owS0NRancyYTZ3QmhDVkFSSXNBQlBlSDF1N3pDM2VRZWRJbVhlcGc5VG1fWWthSDNheUZ5YU5iRVNfOXNfeV9zOW95cUs2VFkybHBFMGFBaWdyRUFMd193Y0I.*_gcl_au*NDkwMDQxNDY4LjE3MDY1Mzg4MzM.&_ga=2.66871598.1246619359.1713190713-497336045.1711559450&_gac=1.217585892.1712065870.Cj0KCQjw2a6wBhCVARIsABPeH1u7zC3eQedImXepg9Tm_YkaH3ayFyaNbES_9s_y_s9oyqK6TY2lpE0aAigrEALw_wcB"
    rel="noopener noreferrer"
    data-testid="help-center"
    onClick={() => {
      handleSupportLinkTracking('support_center', getPDPTrackingLocation(product), product);
    }}
  >
    Learn more here
  </HelpCenterLink>
);

export const getSubscribeButtonText = (stage: ProductStage, isEvergreen?: boolean) => {
  if (isEvergreen && stage !== ProductStage.ComingSoon) {
    return `Notify Me When It's Back`;
  }

  switch (stage) {
    case ProductStage.ComingSoon:
    case ProductStage.Past:
      return 'Subscribe for Creator Updates';
    default:
      return 'Bring it Back!';
  }
};

export const getSubscribeTitle = (stage: ProductStage, isEvergreen?: boolean) => {
  if (isEvergreen) {
    return `Notify Me When It's Back`;
  }

  switch (stage) {
    case ProductStage.ComingSoon:
      return 'Subscribe to Updates!';
    case ProductStage.Past:
      return 'Subscribe for Creator Updates';
    default:
      return 'Bring it Back!';
  }
};

export const getSubscribeDescription = (
  stage: ProductStage,
  isEvergreen?: boolean,
  productTitle?: string,
  creatorName?: string,
) => {
  if (isEvergreen && productTitle) {
    return `Don't miss out on the next batch of the ${productTitle}!`;
  }

  if (stage === ProductStage.Past && creatorName) {
    return `Don't miss out on future product launches from ${creatorName}`;
  }

  switch (stage) {
    case ProductStage.ComingSoon:
      return 'Enter your email to get notified when this product goes live!';
    default:
      return 'Enter your email to get notified if this product ever comes back... 👀';
  }
};

export const scrollToSizeError = () => {
  scroller.scrollTo('size-error', {
    duration: 800,
    smooth: 'easeInOutQuad',
    offset: -200,
  });
};

export const isReadyToShip = (product: Shopify.Product | undefined) => {
  if (!product) return false;
  if (!product.tags) return false;

  const isEvergreen = hasProductTag(product.tags, ProductTag.Evergreen);
  const isLimited = hasProductTag(product.tags, ProductTag.Limited);
  const isHybrid = hasProductTag(product.tags, ProductTag.Hybrid);
  const totalInventory = product.totalInventory ?? 0;

  if (getStage(product.tags) !== ProductStage.Live) return false;
  if (totalInventory <= 0) return false;

  return (isHybrid && !isHybridPreOrder(product)) || (isLimited && !isHybrid) || (isEvergreen && !isHybrid);
};

export const isHybridSoldOut = (product: Shopify.Product | undefined) => {
  if (!product || !product.tags) return false;

  const isHybrid = hasProductTag(product.tags, ProductTag.Hybrid);
  const isPast = hasProductTag(product.tags, ProductTag.Past);
  const totalInventory = product.totalInventory ?? 0;

  return isHybrid && (totalInventory <= 0 || isPast);
};

export const isVariantOrProductReadyToShip = (
  effectiveShipDate: string | null | undefined,
  product: Shopify.Product,
): boolean => {
  if (effectiveShipDate) {
    // If there's a ship date, check if current date is NOT before that date (meaning date has passed)
    return !isCurrentDateBeforeGivenMetafieldDate(effectiveShipDate);
  }
  // Fall back to product-level ready-to-ship check
  return isReadyToShip(product);
};

// Used to calculate ready-to-ship date for evergreen and limited products
// Ship date is +4 days from current date
export const calculateReadyToShipDate = new Date(Date.now() + 4 * 24 * 60 * 60 * 1000);
